import React, { useState } from 'react';
import {
  Pressable,
  Text,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
  View,
  Platform,
  StyleProp,
} from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import { trackButtonClick } from '../../utils/analytics';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'tertiary'
  | 'soft'
  | 'outline-purple';
type IconPosition = 'left' | 'right';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  hoverColor?: string;
  // GTM tracking props
  trackClick?: boolean; // Enable/disable tracking for this button
  trackingLocation?: string; // Location/context where button is used (e.g., 'home-screen', 'pricing-page')
  trackingData?: Record<string, unknown>; // Additional custom data to send with the event
  allowTextWrap?: boolean; // When true, text wraps instead of truncating with ellipsis
  adjustsFontSizeToFit?: boolean; // When true with numberOfLines=1, shrink font to fit (no truncation)
}

const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  style,
  textStyle,
  icon,
  iconPosition = 'left',
  hoverColor,
  trackClick = false,
  trackingLocation,
  trackingData,
  allowTextWrap = false,
  adjustsFontSizeToFit = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { colors } = useTheme();

  // Handle button press with optional GTM tracking
  const handlePress = () => {
    // Track button click if enabled
    if (trackClick && !disabled && !loading) {
      trackButtonClick(title, trackingLocation, {
        button_variant: variant,
        ...trackingData,
      });
    }

    // Execute the original onPress handler
    onPress();
  };

  // Determine button styles based on variant
  const getButtonStyle = () => {
    if (disabled) {
      return [
        styles.button,
        {
          backgroundColor: '#E0D9F7',
          ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
        },
      ];
    }

    let baseStyle;
    switch (variant) {
      case 'secondary':
        baseStyle = [styles.button, { backgroundColor: colors.border }];
        break;
      case 'outline':
        baseStyle = [
          styles.button,
          {
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.theme_color,
          },
        ];
        break;
      case 'danger':
        baseStyle = [styles.button, { backgroundColor: colors.errorText }];
        break;
      case 'tertiary':
        baseStyle = [
          styles.button,
          {
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadowOpacity: 0,
            elevation: 0,
          },
        ];
        break;
      case 'soft':
        baseStyle = [
          styles.button,
          {
            backgroundColor: '#EFE7FD',
            borderWidth: 0,
            shadowOpacity: 0,
            elevation: 0,
          },
        ];
        break;
      case 'outline-purple':
        baseStyle = [
          styles.button,
          {
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderColor: palette.outlinePurple,
            shadowOpacity: 0,
            elevation: 0,
          },
        ];
        break;
      case 'primary':
      default:
        baseStyle = [styles.button, { backgroundColor: colors.theme_color }];
        break;
    }

    return baseStyle;
  };

  // Determine text styles based on variant
  const getTextStyle = () => {
    if (disabled) {
      return [styles.buttonText, { color: '#A59AD6' }];
    }

    switch (variant) {
      case 'secondary':
        return [styles.buttonText, { color: colors.text }];
      case 'outline':
        return [styles.buttonText, { color: colors.theme_color }];
      case 'danger':
        return [
          styles.buttonText,
          { color: colors.onPrimary ?? colors.background },
        ];
      case 'tertiary':
        return [styles.buttonText, { color: palette.outlinePurple }];
      case 'soft':
        return [styles.buttonText, { color: palette.outlinePurple }];
      case 'outline-purple':
        return [styles.buttonText, { color: palette.outlinePurple }];
      case 'primary':
      default:
        return [
          styles.buttonText,
          { color: colors.onPrimary ?? colors.background },
        ];
    }
  };

  const themeFocusOutline =
    Platform.OS === 'web'
      ? {
          outlineWidth: 2,
          outlineStyle: 'solid' as const,
          outlineColor: colors.theme_color || colors.primary,
          outlineOffset: 2,
        }
      : {};

  return (
    <Pressable
      style={({ focused }) => [
        ...getButtonStyle(),
        allowTextWrap && { height: undefined, minHeight: 52 },
        isHovered &&
          !disabled && {
            ...(variant === 'primary' && { backgroundColor: colors.primary }),
            ...(variant === 'secondary' && {
              backgroundColor: colors.borderLight,
            }),
            ...(variant === 'outline' && {
              backgroundColor: hoverColor || colors.theme_color,
              borderColor: colors.theme_color,
            }),
            ...(variant === 'danger' && {
              backgroundColor: colors.errorBackground,
            }),
            ...(variant === 'tertiary' && {
              backgroundColor: palette.outlinePurpleLight,
            }),
            ...(variant === 'soft' && {
              backgroundColor: palette.outlinePurpleSoft,
            }),
            ...(variant === 'outline-purple' && {
              backgroundColor: palette.outlinePurpleLight,
            }),
            ...(style
              ? (() => {
                  const flattenedStyle = StyleSheet.flatten(style);
                  return flattenedStyle?.backgroundColor
                    ? {
                        backgroundColor: flattenedStyle.backgroundColor,
                        opacity: 0.8,
                      }
                    : {};
                })()
              : {}),
          },
        style,
        focused && !disabled && !loading && themeFocusOutline,
      ]}
      onPress={handlePress}
      disabled={disabled || loading}
      // @ts-ignore - onMouseEnter/onMouseLeave are available on web
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={
            disabled
              ? '#A59AD6'
              : variant === 'outline'
                ? colors.theme_color
                : variant === 'tertiary' ||
                    variant === 'soft' ||
                    variant === 'outline-purple'
                  ? palette.outlinePurple
                  : (colors.onPrimary ?? colors.background)
          }
        />
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <View style={styles.iconContainer}>{icon}</View>
          )}
          <Text
            style={[
              ...getTextStyle(),
              // Outline variant: on hover use white text only when background is theme_color; with custom hoverColor keep theme_color text
              variant === 'outline' &&
                isHovered &&
                !disabled && {
                  color: hoverColor
                    ? colors.theme_color
                    : (colors.onPrimary ?? colors.background),
                },
              textStyle,
              icon && iconPosition === 'left' ? { marginLeft: 8 } : {},
              icon && iconPosition === 'right' ? { marginRight: 8 } : {},
              allowTextWrap && { flex: 1, flexShrink: 1, textAlign: 'center' },
            ]}
            numberOfLines={allowTextWrap ? undefined : 1}
            adjustsFontSizeToFit={adjustsFontSizeToFit}
          >
            {title}
          </Text>
          {icon && iconPosition === 'right' && (
            <View style={styles.iconContainer}>{icon}</View>
          )}
        </>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 16,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
    height: 52,
    minWidth: 120,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
    transition: 'background-color 0.2s ease-in',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default Button;
